import {
  DatabasesDisplayPlacePageData,
  DatabasesExternalAssetsDisplayGridRowData,
  GraphDataQualityWarning,
  TableauUser,
  TablesDisplayPlacePageData,
  TablesExternalAssetsDisplayGridRowData
} from '@tableau/data-catalog-client-components';

import { IContentInfo } from '../models/IContentInfo';
import { DataQualityOwner, IDataQualityIndicatorInfo } from '../models/IDataQualityIndicatorInfo';
import { IUserInfo } from '../models/IUserInfo';
import { ILuidItem, INamedItem } from '../ServerApi/types/resourceParts';

type DataQualityWarningSansAsset = Omit<GraphDataQualityWarning, 'asset'>;

export type GraphDatabase = DatabasesExternalAssetsDisplayGridRowData | DatabasesDisplayPlacePageData;
export type GraphTable = TablesExternalAssetsDisplayGridRowData | TablesDisplayPlacePageData;
export type IContentAndLuidInfo = IContentInfo & ILuidItem & INamedItem;

/**
 * The "raw" node types for the external assets grid are mapped directly in the respective resource queries.
 * For content actions, however, we do further mapping to make the catalog types conform to the VPC IContentInfo interface.
 *
 *  There are three places that graph types need to be mapped to vizportalclient types for content actions:
 * 1. [ListViewGridColumnGenerator.graphActionCellWrapper] - to be passed to catalog so it can map the single item on list view
 * 2. [renderDatabasesActionMenu] and [renderTablesActionMenu] - to be passed to catalog so it can map the single item on place page
 * 3. [ExplorerToolbar] which catalog uses by way of [Explorer] <- [ConnectedExplorer] <- [CatalogBrowserSubspaceExplorer]
 *    somehow we've convinced the explorer that the items being selected in the explorer when on database/tables list are
 *    [IExplorableItemTypeInfo] and it would be preferable to map wherever that is, instead of lying to the type system.
 */
export function mapGraphTypeToContentInfoForActions(content: GraphDatabase | GraphTable): IContentAndLuidInfo | null {
  if (content.__typename === 'CustomSQLTable') {
    return null;
  }

  /**
   * This check for dataQualityIndicators needs to be here since we can get a graph type into an action menu
   * so many different ways we usually end up double mapping and we need to prevent that.
   * I believe I've removed all of the double mapping but am keeping this here in case it's accidentally done again.
   */
  if ((content as any as IContentAndLuidInfo).dataQualityIndicators) {
    return content as any as IContentAndLuidInfo;
  }

  // Assert type of DQW array to please compiler when we call `map` later on
  const dqws: ReadonlyArray<DataQualityWarningSansAsset> = content.dataQualityWarnings;

  return {
    ...content,
    contentType: content.__typename === 'DatabaseTable' ? 'table' : 'database',
    certifier: mapUser(content.certifier),
    contact: mapUser(content.contact),
    dataQualityIndicators: dqws?.map(mapGraphDataQualityWarningToVizportalDataQualityIndicator),
    favorite: false,
    updatedAt: new Date(),
    luid: content.luid,
    tags: ('tags' in content ? content.tags : []).map(t => t.name),
  };
}

function mapUser(user: TableauUser | null | undefined): IUserInfo | null {
  return user ? {
    id: null,
    username: user.username,
    displayName: user.name,
  } : null;
}

function mapGraphDataQualityWarningToVizportalDataQualityIndicator(
  dataQualityWarning?: DataQualityWarningSansAsset
): IDataQualityIndicatorInfo | null {
  if (!dataQualityWarning) {
    return null;
  }

  const id = dataQualityWarning.vizportalId;
  const owner = mapAuthorToOwner(dataQualityWarning);
  const type = dataQualityWarning.warningType;

  const dataQualityIndicator = {
    id,
    owner,
    type,
    isSevere: dataQualityWarning.isSevere,
    isActive: dataQualityWarning.isActive,
    message: dataQualityWarning.message,
    updatedAt: dataQualityWarning.updatedAt
  };

  return dataQualityIndicator;
}

function mapAuthorToOwner(warning: DataQualityWarningSansAsset): DataQualityOwner {
  const author = warning.author;

  return {
    displayName: author && author.name || warning.authorDisplayName || '',
    username: author && author.username || '',
    domainName: author && author.domain,
  };
}
